"use client"

import { ChevronsUpDown } from "lucide-react"
import { useTranslations } from "next-intl"

import * as React from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"

type System = {
  name: string
  logo: React.ReactNode
  description: string
}

export function SystemSwitcher({
  systems,
  collapse,
}: {
  systems: System[]
  collapse: boolean
}) {
  const { isMobile } = useSidebar()

  const [activeSystem, setActiveSystem] = React.useState(systems[0])
  const t = useTranslations("navbar")
  if (!activeSystem) {
    return null
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={false}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn("data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground", collapse && "p-0")}
            >
              <div className="sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                {activeSystem.logo}
              </div>
              {!collapse && (
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{activeSystem.name}</span>
                  <span className="truncate text-xs">{activeSystem.description}</span>
                </div>
              )}
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {t("switcher.systems")}
            </DropdownMenuLabel>
            {systems.map((system, index) => (
              <DropdownMenuItem
                key={system.name}
                onClick={() => setActiveSystem(system)}
                className="gap-2 p-2"
              >
                <div className="flex size-6 items-center justify-center rounded-md border">
                  {system.logo}
                </div>
                {system.name}
                <DropdownMenuShortcut>
                  {t("switcher.sidebar_shortcut")}
                  {index + 1}
                </DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
